import { IDestroyOptions, Rectangle, Sprite, Text, Texture } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { IFocusable } from "../../../layers/IFocusable";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { PiggyInfoResourcePackage } from "../resources/PiggyInfoResourcePackage";
import { PiggyInfoType } from "../components/PiggyInfoType";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { gsap } from "gsap";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { PiggyInfoViewConfig, PiggyInfoViewDescription } from "../components/PiggyInfoViewConfig";

export class PiggyInfoPopupView extends BaseScalableView<void> implements IFocusable {
    private readonly commonResources: CommonResourcesPackage;
    private readonly piggyResources: PiggyInfoResourcePackage;
    private readonly animations: gsap.core.Timeline;
    private readonly viewConfig: PiggyInfoViewDescription;

    private leftLight: Sprite;
    private rightLight: Sprite;
    private bottomLight: Sprite;
    private closeButton: ButtonWithScaleAnimation;
    private fadeScreen: FadeScreen;

    private glass: Sprite;
    private innerText: Text;
    private headerText: Text;
    private icon: Sprite;

    public readonly focusable: boolean = true;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        piggyResources: PiggyInfoResourcePackage,
        type: PiggyInfoType
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.piggyResources = piggyResources;
        this.viewConfig = PiggyInfoViewConfig.VIEW_CONFIG.get(type);
        this.animations = new gsap.core.Timeline({ paused: true });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.leftLight = new Sprite(this.piggyResources.assets_views_piggy_info_blue_light_png);
        this.rightLight = new Sprite(this.piggyResources.assets_views_piggy_info_blue_light_png);
        this.rightLight.scale.x = -1;
        this.bottomLight = new Sprite(this.piggyResources.assets_views_piggy_info_green_light_png);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.glass = new Sprite(this.piggyResources[this.viewConfig.glassTexture as keyof PiggyInfoResourcePackage] as Texture);
        this.icon = new Sprite(this.piggyResources[this.viewConfig.iconTexture as keyof PiggyInfoResourcePackage] as Texture);
        this.headerText = new Text(this.viewConfig.headerTextText, this.viewConfig.headerTextTextStyle);
        this.headerText.skew.x = -0.08;
        this.headerText.angle = -5;
        this.innerText = new Text(this.viewConfig.innerTextText, this.viewConfig.innerTextTextStyle);

        this.addChild(
            this.fadeScreen,
            this.leftLight,
            this.rightLight,
            this.bottomLight,
            this.closeButton,
            this.glass,
            this.icon,
            this.headerText,
            this.innerText,
        );
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.leftLight.position.set(0, 305);
        this.rightLight.position.set(width, 305);
        this.bottomLight.position.set(0, 1470);
        this.glass.position.set(this.viewConfig.glassPosition.x, this.viewConfig.glassPosition.y);
        this.icon.position.set(this.viewConfig.iconPosition.x, this.viewConfig.iconPosition.y);
        this.headerText.position.set(this.viewConfig.headerTextPosition.x, this.viewConfig.headerTextPosition.y);
        this.innerText.position.set(width * 0.5 - this.innerText.width * 0.5, this.viewConfig.innerTextPosition.y);
        this.closeButton.position.set(this.viewConfig.closeButtonPosition.x, this.viewConfig.closeButtonPosition.y);
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        this.closeButton.eventMode = 'auto';
        await this.hide();
        this.closeButton.eventMode = 'static';
        this.operationControls.complete();
    }

    private async show(): Promise<void> {
        await this.fadeScreen.fadeIn();
    }

    private async hide(): Promise<void> {
        this.animations.to(this, { alpha: 0, duration: 0.2, ease: "power1.out" });
        await this.animations.play();
        this.animations.clear();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        super.destroy(options);
    }
}